/**
 * OPS-11: Critical Alerts Panel — Command Center Edition
 *
 * Displays active operational alerts sorted by severity.
 * Critical alerts use prominent visual treatment.
 * Supports drill-down navigation to related staff pages.
 */

import { useNavigate } from 'react-router-dom';
import type { OperationalAlert, AlertSeverity } from '../../services/controlTowerTypes';
import { getServiceImpact, getImpactStyle } from './serviceImpactTags';
import { drillRouteForAlertType } from '../../../utils/drillRoutes';
import { InfoTooltip } from './InfoTooltip';

const SEVERITY_CONFIG: Record<AlertSeverity, { badge: string; border: string; icon: string }> = {
  critical: { badge: 'bg-rose-500/20 text-rose-400 border-rose-500/30', border: 'border-l-rose-500', icon: '🔴' },
  warning:  { badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30', border: 'border-l-amber-500', icon: '🟡' },
  info:     { badge: 'bg-sky-500/20 text-sky-400 border-sky-500/30', border: 'border-l-sky-500', icon: '🔵' },
};

const SEVERITY_ORDER: AlertSeverity[] = ['critical', 'warning', 'info'];

interface AlertsPanelProps {
  alerts: OperationalAlert[];
  onAlertClick?: (alert: OperationalAlert) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function AlertsPanel({ alerts, onAlertClick, collapsed, onToggleCollapse }: AlertsPanelProps) {
  const navigate = useNavigate();

  const sevIdx = (s: AlertSeverity) => { const i = SEVERITY_ORDER.indexOf(s); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
  const sorted = [...alerts].sort(
    (a, b) => sevIdx(a.severity) - sevIdx(b.severity),
  );

  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const warningCount = alerts.filter((a) => a.severity === 'warning').length;

  return (
    <div className={`rounded-2xl bg-slate-800/50 border border-slate-700/50 p-5 shadow-lg${collapsed ? '' : ' min-h-[260px]'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5">Alerts <InfoTooltip text="Operational alerts detect critical situations requiring immediate attention — from equipment failures and safety concerns to service SLA breaches and guest escalations." /></h2>
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="text-xs font-bold bg-rose-500/20 text-rose-400 px-2 py-0.5 rounded-full">
              {criticalCount} critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="text-xs font-bold bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
              {warningCount} warning
            </span>
          )}
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand alerts panel' : 'Collapse alerts panel'}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              {collapsed ? '▸ Expand' : '▾ Collapse'}
            </button>
          )}
        </div>
      </div>

      {/* Alert list */}
      {collapsed ? null : sorted.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-2xl mb-2">✅</div>
          <p className="text-slate-500 text-sm">No active alerts</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {sorted.map((alert) => {
            const config = SEVERITY_CONFIG[alert.severity];
            const drillRoute = drillRouteForAlertType(alert.type);
            return (
              <div
                key={alert.id}
                role="button"
                tabIndex={0}
                aria-label={`View alert: ${alert.title}`}
                onClick={() => onAlertClick?.(alert)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAlertClick?.(alert); } }}
                className={`w-full text-left rounded-xl bg-slate-900/50 border-l-4 ${config.border} p-3 hover:bg-slate-900/80 transition-colors group cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-sm mt-0.5">{config.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-slate-200 truncate">{alert.title}</span>
                      <span className={`flex-shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${config.badge}`}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 line-clamp-2">{alert.explanation}</p>
                    {(() => { const impact = getServiceImpact(alert.type); const s = getImpactStyle(impact); return (
                      <span className={`inline-flex text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mt-1.5 ${s.bg} ${s.text}`}>
                        {impact}
                      </span>
                    ); })()}
                    <button
                      type="button"
                      aria-label={`Navigate to ${drillRoute.replace('/', '').replace('-', ' ')}`}
                      onClick={(e) => { e.stopPropagation(); navigate(drillRoute); }}
                      className="mt-1.5 text-[11px] text-slate-500 hover:text-emerald-400 transition-colors flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-sky-500 rounded"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      Go to {drillRoute.replace('/', '').replace('-', ' ')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
